import {Controller} from '@hotwired/stimulus';

const ITEMS = 'button, [role="button"]';

/**
 * Barre d'outils (modèle « toolbar » de l'APG), posée sur un `role="toolbar"` : une seule entrée dans l'ordre de
 * tabulation, le premier outil puis le dernier atteint ; flèches gauche et droite, Début et Fin d'un outil à
 * l'autre, rangées comprises ; Tab en sort. Un outil masqué ou désactivé est sauté.
 *
 * Les outils ajoutés après coup en font partie, dont la barre de CKEditor rendue dedans par `sharedSpaces`
 * (bandeau de `rich-text.md`) : ses groupes `toolbar` deviennent des `group`, et sa navigation propre (Tab de
 * groupe en groupe, flèches qui bouclent dans un groupe) cède la place à celle de la barre. Haut et Bas restent
 * à l'outil de CKEditor qui ouvre une liste (`aria-haspopup`).
 */
export default class extends Controller {
  connect() {
    this.navigate = this.navigate.bind(this);
    this.remember = this.remember.bind(this);

    // en capture : CKEditor écoute les touches sur ses outils, l'événement ne doit pas les atteindre
    this.element.addEventListener('keydown', this.navigate, true);
    this.element.addEventListener('focusin', this.remember);

    this.observer = new MutationObserver(() => this.scheduleSync());
    this.observer.observe(this.element, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'hidden', 'disabled', 'aria-disabled'],
    });

    this.sync();
  }

  disconnect() {
    this.element.removeEventListener('keydown', this.navigate, true);
    this.element.removeEventListener('focusin', this.remember);
    this.observer.disconnect();
    cancelAnimationFrame(this.frame);
  }

  navigate(event) {
    const item = event.target.closest(ITEMS);

    if (item === null || !this.element.contains(item)) {
      return;
    }

    // CKEditor pose aria-haspopup="false" sur ses boutons sans liste
    const popup = !['false', null].includes(item.getAttribute('aria-haspopup'));
    const vertical = event.key === 'ArrowUp' || event.key === 'ArrowDown';

    if (event.key === 'Tab' || (vertical && !popup)) {
      if (item.closest('.cke') !== null) {
        event.stopPropagation();
      }

      return;
    }

    const items = this.visibleItems;

    if (items.length === 0) {
      return;
    }

    // un outil désactivé peut avoir le focus (CKEditor, souris) : on repart de sa place dans la barre
    const following = items.findIndex((other) => item === other ||
      (item.compareDocumentPosition(other) & Node.DOCUMENT_POSITION_FOLLOWING));
    const at = following === -1 ? items.length : following;
    const rtl = getComputedStyle(this.element).direction === 'rtl';
    const previous = (at - 1 + items.length) % items.length;
    const next = (items[at] === item ? at + 1 : at) % items.length;

    const target = {
      ArrowLeft: rtl ? next : previous,
      ArrowRight: rtl ? previous : next,
      Home: 0,
      End: items.length - 1,
    }[event.key];

    if (target === undefined) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    items[target].focus();
  }

  remember(event) {
    const item = event.target.closest(ITEMS);

    if (item !== null && this.element.contains(item)) {
      this.current = item;
      this.reached = true;
      this.sync();
    }
  }

  scheduleSync() {
    cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(() => this.sync());
  }

  sync() {
    this.element.querySelectorAll('[role="toolbar"]').forEach((group) => group.setAttribute('role', 'group'));

    const enabled = this.enabledItems;
    const visible = enabled.filter(isVisible);

    // avant tout outil atteint, l'entrée est le premier, même s'il s'active après les autres (CKEditor) ; barre
    // masquée (onglet de langue inactif, section repliée) : un outil reste dans l'ordre de tabulation pour le jour
    // où elle s'affiche, ce qui ne déclenche aucune mutation à l'intérieur
    if (!this.reached || !(visible.length > 0 ? visible : enabled).includes(this.current)) {
      this.current = visible[0] ?? enabled[0];
    }

    this.element.querySelectorAll(ITEMS).forEach((item) => {
      item.tabIndex = item === this.current ? 0 : -1;
    });
  }

  get enabledItems() {
    return [...this.element.querySelectorAll(ITEMS)]
      // une liste de CKEditor porte son état désactivé sur son conteneur
      .filter((item) => !item.disabled && item.closest('[aria-disabled="true"]') === null);
  }

  get visibleItems() {
    return this.enabledItems.filter(isVisible);
  }
}

function isVisible(element) {
  return element.getClientRects().length > 0;
}
